import {DiaryRowAppointment} from "../components/dayView/DayViewTimeLine";
import Appointment from "../model/appointment/Appointment";
import {AppointmentDataType} from "../model/appointment/AppointmentDetails";
import {Option} from "ts-option";
import JobAppointment from "../model/appointment/job/JobAppointment";
import TextUtils from "./TextUtils";
import moment = require("moment");

export default class AppointmentUtils {

	/**
	 * Split list of appointment diary rows into colliding lists.
	 * @param {DiaryRowAppointment[]} diaryRowAppointments
	 * @return {Array<DiaryRowAppointment[]>}
	 */
	public static splitDiaryRowsIntoCollisionGroups(diaryRowAppointments: DiaryRowAppointment[]): Array<DiaryRowAppointment[]> {
		let groups: Array<DiaryRowAppointment[]> = [];
		let indexToCompareWith = 1;
		const sorted: DiaryRowAppointment[] = diaryRowAppointments.sort(
			(a, b) => moment(b.appointment.dateTime).isAfter(a.appointment.dateTime) ? -1 : 1);
		if (sorted.length === 1) {
			groups.push(sorted);
		}
		else {
			let tmpGroup: DiaryRowAppointment[] = [sorted[0]];
			for (let firstItemInGroup = 0; firstItemInGroup < sorted.length - 1 && indexToCompareWith <= sorted.length - 1;
				indexToCompareWith++) {
				// check if first appointment in group collides with next
				if (AppointmentUtils.timeCollision(sorted[firstItemInGroup].appointment,
					sorted[indexToCompareWith].appointment, 15)) {
					// if so, add this appointment to the same colliding group
					tmpGroup.push(sorted[indexToCompareWith]);
					if (indexToCompareWith == sorted.length - 1) {
						// if we reach the end, add all remaining items to the same group
						groups.push(sorted.slice(firstItemInGroup, indexToCompareWith + 1));
						firstItemInGroup = indexToCompareWith;
					}
				}
				// check if items collide with any appointments in current group
				else if (AppointmentUtils.timeCollisionInGroup(sorted[indexToCompareWith].appointment,
					tmpGroup.map(a => a.appointment),
					15)) {
					// if so, add this appointment to the same colliding group
					tmpGroup.push(sorted[indexToCompareWith]);
					if (indexToCompareWith == sorted.length - 1) {
						// if we reach the end, add all remaining items to the same group
						groups.push(sorted.slice(firstItemInGroup, indexToCompareWith + 1));
						firstItemInGroup = indexToCompareWith;
					}
				}
				// if appointment doesn't collide with next or any previous colliding appointments, it means it start new group
				else {
					// store current colliding group
					groups.push(tmpGroup.length ? tmpGroup : [sorted[indexToCompareWith]]);
					tmpGroup = [];
					// start new colliding group
					tmpGroup.push(sorted[indexToCompareWith]);
					if (indexToCompareWith == sorted.length - 1) {
						// if we reach the end, add all remaining items to the same group
						groups.push(tmpGroup);
					}
					firstItemInGroup = indexToCompareWith;
				}
			}
		}
		return groups;
	}

	/**
	 * Indicates if appointments overlaps in some interval of time..
	 * @param {moment.Moment} first appointment.
	 * @param {moment.Moment} second appointment.
	 * @param {number} interval interval in minutes of the overlapping to check.
	 * @return {boolean} true when time slots overlaps.
	 */
	public static timeCollision(first: AppointmentDataType, second: AppointmentDataType, interval: number): boolean {
		const firstDuration = (first instanceof Appointment) ? 2 : 8;
		const secondDuration = (second instanceof Appointment) ? 2 : 8;
		if (moment(first.dateTime).isSame(second.dateTime, "minute")) {
			return true;
		}
		else if (moment(first.dateTime).isBefore(second.dateTime)) {
			return moment(first.dateTime).add(firstDuration, "h").subtract(interval, "m").isAfter(second.dateTime,
				"minute");
		}
		else {
			return moment(first.dateTime).isBefore(
				moment(second.dateTime).add(secondDuration, "h").subtract(interval, "m"), "minute");
		}
	}

	/**
	 * Find time collision with any appointment in group.
	 * @param {AppointmentDataType} appointment appointment to check for collision.
	 * @param {AppointmentDataType[]} group group of appointments to check for collision in.
	 * @param {number} interval interval in minutes of the overlapping to check.
	 * @returns {boolean} true if appointment overlaps with any other appointment in group.
	 */
	public static timeCollisionInGroup(appointment: AppointmentDataType, group: AppointmentDataType[], interval: number) {
		return group.some((v) => AppointmentUtils.timeCollision(v, appointment, interval));
	}

	public static getAppointmentDurations() {
		const arr: IAppointmentDuration[] = [];
		for (let i = 1; i < 8; i++) {
			arr.push({minutes: (i * 60), displayValue: `${i} ${TextUtils.addSIfPlural("hour", i)}`});

			["15", "30", "45"].forEach((value, j) => {
				arr.push({
					minutes: i * 60 + (j + 1) * 15,
					displayValue: `${i} ${TextUtils.addSIfPlural("hour", i)} ${value} mins`
				})
			})
		}
		arr.push({minutes: 8 * 60, displayValue: "All day (8 hrs)"});
		return arr;
	}

	public static isNextDay(dt: Option<moment.Moment>, duration: number) {
		return dt.map(dt => moment(dt).add(duration, "minutes").add(-1, "s").isAfter(dt, "day")).getOrElse(false);
	}

	public static isCollisionWithAppointment(duration: number,
		selectedDate: moment.Moment,
		appointments: (Appointment | JobAppointment)[]
	): boolean {
		return appointments.filter(a => a.dateTime.isSame(selectedDate, "day"))
			.filter(a => selectedDate.isBefore(a.dateTime)
				&& moment(selectedDate).add(duration - 1, "minutes").isSameOrAfter(a.dateTime, "minutes"))
			.length > 0;
	}
}

export interface IAppointmentDuration {
	minutes: number,
	displayValue: string
}